import { Entity } from '@api';
import { getHorizonToIndexMap } from '@terminal/components/price-movement/components/price-movement-chart/utils/price-movement-chart-utils';
import { getActiveFilterOptions } from '@terminal/hooks/use-filter-actions/use-filter-actions-utils';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import {
  useSpxBenchmark,
  useThemePerformance,
} from '@terminal/react-query-hooks/themes/useThemeOverview';
import { usePerformanceStore } from '@terminal/views/theme-overview/tab-content/performance/hooks/usePerformanceStore';
import { useScreenerEntitiesTags } from '@terminal/widgets/scenario/hooks/use-screener-entities-tags/useScreenerEntitiesTags';
import { useMemo } from 'react';

export const usePerformanceFiltersData = ({ tags }: { tags: string[] }) => {
  const { filterGroups } = usePerformanceStore(state => ({
    filterGroups: state.filterGroups,
  }));
  const numDays = getHorizonToIndexMap(1).get('1y');
  const filterOptions = getActiveFilterOptions(filterGroups);

  // const companiesThemeTags = companiesTheme.map(c => c.company);
  const defaultOptionsMapped = !!tags.length
    ? {
        entity_tag: {
          values: [{ tags: tags }],
        },
      }
    : undefined;

  const {
    data: allEntities,
    isFetching: isScreenerEntitiesFetching,
    isError: isScreenerEntitiesError,
    error: screenerEntitiesError,
  } = useScreenerEntitiesTags({
    filterOptions: filterOptions,
    defaultOptionsMapped,
  });

  const {
    data,
    isFetching: isThemeFetching,
    isError,
    error,
  } = useThemePerformance(allEntities?.entities ?? [], numDays);
  const { data: entitiesData } = useEntities({
    entityTags: tags,
  });

  const { data: spxData, isFetching: isSpxFetching } = useSpxBenchmark(
    data?.aggregate[0].index
  );

  const entityTags = useMemo(
    () => allEntities?.entities ?? [],
    [allEntities?.entities]
  );

  const missingEntities = useMemo(() => {
    return tags.reduce((acc, tag) => {
      if (!(data?.allEntityTags ?? []).includes(tag)) {
        const foundEntity = entitiesData.find(e => e.tag === tag);

        if (foundEntity) {
          acc.push(foundEntity);
        }
      }

      return acc;
    }, [] as Entity[]);
  }, [tags, data?.allEntityTags, entitiesData]);

  return {
    entityTags,
    themeData: data,
    spxData,
    entitiesData,
    isFetching: isScreenerEntitiesFetching || isThemeFetching || isSpxFetching,
    isError: isError || isScreenerEntitiesError,
    error: error || screenerEntitiesError,
    missingEntities,
  };
};
